// Content loader — generalizes the single-section blog loader pattern across
// every section in CONTENT-SPEC §1. Frontmatter is read eagerly (so indexes,
// routes and footers have metadata without loading a single body chunk); the
// MDX component itself is loaded lazily via import.meta.glob, one chunk per page.
//
// The eager side comes from `virtual:content-meta` (scripts/lib/vite-content-meta.ts),
// which parses each file's YAML at build time. An eager `import.meta.glob`
// with `import: 'frontmatter'` would do the same job but is a static import of
// every MDX module, which stops Rollup splitting the bodies into chunks.

import type { ComponentType } from 'react';
import type { Frontmatter } from './frontmatter';
import contentMeta from 'virtual:content-meta';
import staticPages from './static-pages.json';

export const SECTIONS = ['upgrades', 'migrations', 'support', 'sectors', 'notes'] as const;
export type Section = (typeof SECTIONS)[number];

export const SECTION_LABEL: Record<Section, string> = {
  upgrades: 'Upgrades',
  migrations: 'Migrations',
  support: 'Support',
  sectors: 'Sectors',
  notes: 'Field notes',
};

export interface ContentMeta extends Frontmatter {
  section: Section;
  slug: string;
  path: string;
}

const componentModules = import.meta.glob<{ default: ComponentType }>('../content/*/*.mdx');

const parsePath = (p: string): { section: Section; slug: string } | null => {
  const m = p.match(/\/content\/([^/]+)\/([^/]+)\.mdx$/);
  if (!m) return null;
  const section = m[1] as Section;
  if (!SECTIONS.includes(section)) {
    console.warn(`[content] ignoring ${p}: "${m[1]}" is not a section (${SECTIONS.join(', ')})`);
    return null;
  }
  return { section, slug: m[2] };
};

const all: ContentMeta[] = [];
for (const entry of contentMeta) {
  const { section, slug, file, path: _path, ...fm } = entry;
  const p = file;
  if (!SECTIONS.includes(section as Section)) {
    console.warn(`[content] ignoring ${p}: "${section}" is not a section (${SECTIONS.join(', ')})`);
    continue;
  }
  const loc = { section: section as Section, slug };
  // The Vite content plugin validates and normalizes every entry at build time.
  const d = fm as Frontmatter;
  all.push({ ...d, section: loc.section, slug: loc.slug, path: `/${loc.section}/${loc.slug}` });
}

// Newest `updated` first, slug as a stable tiebreaker so SSR and client agree.
all.sort((a, b) => b.updated.localeCompare(a.updated) || a.slug.localeCompare(b.slug));

export const allContent: readonly ContentMeta[] = all;

export const contentBySection = (section: Section): ContentMeta[] =>
  allContent.filter((c) => c.section === section);

export const contentByPath = (path: string): ContentMeta | undefined =>
  allContent.find((c) => c.path === path.replace(/\/$/, ''));

export const latestNotes = (n: number) => contentBySection('notes').slice(0, n);

export const contentComponents: Record<string, () => Promise<{ default: ComponentType }>> =
  Object.fromEntries(
    Object.entries(componentModules).flatMap(([p, imp]) => {
      const loc = parsePath(p);
      return loc ? [[`/${loc.section}/${loc.slug}`, imp]] : [];
    }),
  );

// Engine-owned page metadata (title/description) shared with the build scripts.
export interface StaticPage {
  path: string;
  title: string;
  description: string;
}
export const staticPageOrNull = (path: string): StaticPage | null =>
  (staticPages.pages as StaticPage[]).find((x) => x.path === (path.replace(/\/$/, '') || '/')) ?? null;
export const staticPage = (path: string): StaticPage => {
  const p = (staticPages.pages as StaticPage[]).find((x) => x.path === path);
  if (!p) throw new Error(`[content] no static-pages.json entry for ${path}`);
  return p;
};
